/**
 * Tâche 1493 (Eric 2026-06) — Lire l'état réel des étapes, et le dire.
 *
 * Fonctions pures, sans React : elles traduisent la réponse de
 * `GET /api/videos/{id}/pipeline/etapes` en couleur de pastille et en
 * libellé de menu. Elles vivent à part parce que TROIS écrans s'en servent
 * (fiche vidéo, Explorateur, File d'attente) : trois copies auraient fini
 * par nommer différemment la même étape.
 *
 * ⚠️ « Faite » et « à jour » sont deux faits distincts. Une fusion faite
 * avant un nouvel import Fireflies est faite, mais elle porte sur un texte
 * remplacé : la montrer verte ferait croire à l'utilisateur que son travail
 * est à jour. D'où trois états, choix explicite d'Eric.
 */

/** "a_jour" | "perimee" | "jamais" — l'état d'une étape pour la pastille. */
export function etatEtape(detail) {
  if (!detail || !detail.faite) return "jamais";
  return detail.a_jour === true ? "a_jour" : "perimee";
}

/** Les étapes du bandeau que le serveur sait vérifier (tâche 1493). */
export const ETAPES_VERIFIEES = {
  fusion: "fusion",
  extraction: "extraction",
  embedding: "indexation",
};

export function formaterDate(iso, lang) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString(lang === "en" ? "en-CA" : "fr-CA",
      { dateStyle: "medium", timeStyle: "short" });
  } catch (_) {
    return "";
  }
}

/**
 * Pourquoi cette étape est ambre, en une phrase.
 * ⚠️ « On ne sait pas » n'est pas « c'est à jour » : une étape faite avant
 * que l'empreinte existe le dit, au lieu de se faire passer pour fraîche.
 */
export function raisonEtape(detail, lang) {
  const tr = (fr, en) => (lang === "en" ? en : fr);
  if (!detail) return "";
  switch (detail.raison) {
    case "source_modifiee":
      return tr("La source a changé depuis : refaire cette étape donnera un autre résultat.",
        "The source has changed since: redoing this step will give a different result.");
    case "fraicheur_inconnue":
      return tr("Faite avant que l'on enregistre la source utilisée — impossible de dire si elle est à jour.",
        "Done before we started recording the source used — we cannot tell whether it is up to date.");
    case "connaissances_modifiees":
      return tr(`${detail.a_indexer} connaissance(s) sans index ou modifiée(s) depuis.`,
        `${detail.a_indexer} knowledge item(s) not indexed or changed since.`);
    default:
      return "";
  }
}

/**
 * Le libellé d'une option du menu : « Faire » ou « Refaire » selon ce qui a
 * réellement été fait, exactement comme Eric l'a demandé.
 */
export function actionEtape(cle, etapes, lang) {
  const tr = (fr, en) => (lang === "en" ? en : fr);
  const detail = (etapes || {})[cle] || {};
  const refaire = !!detail.faite;
  const quand = formaterDate(detail.le, lang);
  const deja = quand
    ? tr(`Déjà faite le ${quand}.`, `Already done on ${quand}.`)
    : tr("Déjà faite (date inconnue).", "Already done (date unknown).");

  if (cle === "fusion") {
    return {
      refaire,
      // ⚠️ Le libellé ne dit pas « et poursuivre » : chaque option fait UNE
      // étape puis rend la main (choix d'Eric). Un bouton qui annonce la
      // suite et s'arrête ferait attendre pour rien.
      label: refaire
        ? tr("Refaire la fusion des transcriptions", "Redo the transcript fusion")
        : tr("Faire la fusion des transcriptions", "Run the transcript fusion"),
      desc: `${refaire ? `${deja} ${raisonEtape(detail, lang)} ` : ""}${tr(
        "Compare Whisper et Fireflies, puis le pipeline se met en pause pour que vous choisissiez la suite.",
        "Compares Whisper and Fireflies, then the pipeline pauses so you can choose what comes next.",
      )}`,
      empeche: detail.possible === false
        ? tr("Aucune transcription à fusionner.", "No transcript to merge.")
        : null,
    };
  }
  if (cle === "extraction") {
    return {
      refaire,
      label: refaire
        ? tr("Refaire l'extraction des connaissances", "Redo the knowledge extraction")
        : tr("Faire l'extraction des connaissances", "Run the knowledge extraction"),
      desc: `${refaire ? `${deja} ${tr(`${detail.compte} connaissance(s) existent.`, `${detail.compte} knowledge item(s) exist.`)} ${raisonEtape(detail, lang)} ` : ""}${
        detail.source === "fusionnee"
          ? tr("Génère Q&R, Scripts, Procédures, Objections, Concepts et Synthèse à partir de la transcription fusionnée.",
            "Generates Q&A, scripts, procedures, objections, concepts and synthesis from the merged transcript.")
          : tr("Génère Q&R, Scripts, Procédures, Objections, Concepts et Synthèse à partir de la transcription Whisper (aucune fusion n'existe).",
            "Generates Q&A, scripts, procedures, objections, concepts and synthesis from the Whisper transcript (no fusion exists).")
      }`,
      empeche: detail.possible === false
        ? tr("Aucune transcription disponible.", "No transcript available.")
        : null,
    };
  }
  // Indexation de la base.
  // ⚠️ `detail` est VIDE pendant les 100-300 ms entre l'ouverture du menu et
  // la réponse du serveur (l'Explorateur ne charge l'état qu'au clic). Sans
  // cette garde, l'option affichait « Rend undefined connaissance(s)
  // trouvable(s) … sur undefined » — défaut vu par la relecture du 2026-10-07.
  const inconnu = detail.total === undefined;
  const rien = !inconnu && detail.possible !== false && detail.a_indexer === 0;
  return {
    refaire,
    label: refaire
      ? tr("Réindexer la base", "Re-index the database")
      : tr("Indexer la base", "Index the database"),
    desc: detail.dernier_echec
      // ⚠️ La dernière tentative a échoué : le dire ICI, dans le menu, et pas
      // seulement dans le journal du pipeline. Sinon on reclique sans fin.
      ? `${tr("Dernière tentative en échec", "Last attempt failed")} (${formaterDate(detail.dernier_echec_le, lang)}) : ${detail.dernier_echec}`
      : inconnu
        ? tr("Lecture de l'état des connaissances…", "Reading the knowledge state…")
        : rien
          ? tr(`Tout est déjà indexé (${detail.total} connaissance(s)) — rien à refaire.`,
            `Everything is already indexed (${detail.total} item(s)) — nothing to redo.`)
          : tr(`Rend ${detail.a_indexer} connaissance(s) trouvable(s) par le robot conversationnel (sur ${detail.total}).`,
            `Makes ${detail.a_indexer} knowledge item(s) findable by the chatbot (out of ${detail.total}).`),
    // ⚠️ Choix d'Eric : on ne réindexe QUE ce qui manque ou a changé. Quand
    // rien ne manque, l'option est inerte plutôt que de facturer des appels
    // d'embedding pour un résultat identique. Tant que l'état n'est pas
    // revenu, l'option est inerte aussi : lancer une indexation sans savoir
    // ce qu'elle touchera est précisément ce qu'on veut éviter.
    empeche: detail.possible === false
      ? tr("Aucune connaissance à indexer — lancez d'abord l'extraction.",
        "No knowledge to index — run the extraction first.")
      : inconnu
        ? tr("Lecture de l'état des connaissances…", "Reading the knowledge state…")
        : (rien ? tr("Tout est déjà indexé.", "Everything is already indexed.") : null),
  };
}
